import React, { useCallback, useState } from 'react';
import { adminSkillLevelAlignmentAPI } from '../services/adminApi';

const btn = {
  padding: '0.5rem 1rem',
  borderRadius: 8,
  border: 'none',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '0.85rem',
};

const AdminSkillLevelAlignment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openingAudit, setOpeningAudit] = useState(null);
  const [finalAudit, setFinalAudit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmName, setConfirmName] = useState('');

  const loadOpeningAudit = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminSkillLevelAlignmentAPI.getOpeningAudit();
      setOpeningAudit(res?.data);
      setSuccess('Opening Season audit loaded (read-only).');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Opening audit failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFinalAudit = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminSkillLevelAlignmentAPI.getFinalSprintAudit();
      setFinalAudit(res?.data);
      setSuccess('Final Sprint audit loaded (read-only).');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Final Sprint audit failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const seasonId = finalAudit?.season?.id;
      const res = await adminSkillLevelAlignmentAPI.getFinalSprintPreview(
        seasonId ? { season_id: seasonId } : {}
      );
      setPreview(res?.data);
      setSuccess('Final Sprint dry-run preview loaded (no writes).');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  }, [finalAudit]);

  const repairOpening = async (dryRun) => {
    setLoading(true);
    setError('');
    try {
      const res = await adminSkillLevelAlignmentAPI.repairOpening({ dryRun });
      const count = res?.data?.count ?? 0;
      setSuccess(
        dryRun
          ? `Opening dry-run: ${count} profile→roster cascade repair(s) would run.`
          : `Opening repair applied: ${count} cascade(s).`
      );
      await loadOpeningAudit();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Opening repair failed');
    } finally {
      setLoading(false);
    }
  };

  const applyFinalSprint = async (dryRun) => {
    const seasonId = preview?.season?.id || finalAudit?.season?.id;
    if (!seasonId) {
      setError('Load Final Sprint audit first.');
      return;
    }
    if (!dryRun) {
      const expected = preview?.season?.name || finalAudit?.season?.name || '';
      if (confirmName.trim() !== expected.trim()) {
        setError(`Type the exact season name to confirm: "${expected}"`);
        return;
      }
    }
    setLoading(true);
    setError('');
    try {
      const res = await adminSkillLevelAlignmentAPI.applyFinalSprint({
        seasonId,
        dryRun,
        confirmSeasonName: dryRun ? undefined : confirmName.trim(),
      });
      const applied = res?.data?.applied?.length ?? 0;
      setSuccess(
        dryRun
          ? `Final Sprint dry-run: ${applied} profile update(s) would run (players.skill_level only).`
          : `Final Sprint apply: ${applied} profile skill(s) updated. Roster/standings unchanged.`
      );
      if (!dryRun) await loadFinalAudit();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Apply failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '1.25rem', maxWidth: 1100 }}>
      <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.25rem' }}>Skill level alignment</h2>
      <p style={{ margin: '0 0 1rem', color: '#6b7280', fontSize: '0.88rem', maxWidth: 720 }}>
        <strong>Opening Season (registration open):</strong> first-time-paid players — profile edits
        cascade to roster and team average. Use repair to fix existing mismatches.
        <br />
        <strong>Final Sprint (completed):</strong> read-only audit then profile-only repair from played
        division <code>skill_level_min</code> — does not change roster, team avg, divisions, or standings.
      </p>

      {error ? (
        <div style={{ marginBottom: '0.75rem', padding: '0.65rem', background: '#fef2f2', color: '#991b1b', borderRadius: 8 }}>
          {error}
        </div>
      ) : null}
      {success ? (
        <div style={{ marginBottom: '0.75rem', padding: '0.65rem', background: '#ecfdf5', color: '#065f46', borderRadius: 8 }}>
          {success}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button type="button" style={{ ...btn, background: '#374151', color: '#fff' }} disabled={loading} onClick={loadOpeningAudit}>
          Audit Opening (read-only)
        </button>
        <button type="button" style={{ ...btn, background: '#6b7280', color: '#fff' }} disabled={loading} onClick={() => repairOpening(true)}>
          Opening repair dry-run
        </button>
        <button type="button" style={{ ...btn, background: '#dc2626', color: '#fff' }} disabled={loading} onClick={() => repairOpening(false)}>
          Apply Opening repair
        </button>
      </div>

      {openingAudit?.summary ? (
        <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '1rem' }}>
          Opening: {openingAudit.summary.profileRosterMismatchCount} profile≠roster mismatch(es),{' '}
          {openingAudit.summary.teamAvgMismatchCount} team avg issue(s)
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button type="button" style={{ ...btn, background: '#374151', color: '#fff' }} disabled={loading} onClick={loadFinalAudit}>
          Audit Final Sprint (read-only)
        </button>
        <button type="button" style={{ ...btn, background: '#6b7280', color: '#fff' }} disabled={loading} onClick={loadPreview}>
          Final Sprint dry-run preview
        </button>
        <button type="button" style={{ ...btn, background: '#6b7280', color: '#fff' }} disabled={loading} onClick={() => applyFinalSprint(true)}>
          Final Sprint apply dry-run
        </button>
      </div>

      {finalAudit?.summary ? (
        <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.75rem' }}>
          Final Sprint: {finalAudit.summary.wouldUpdateCount} profile alignment(s),{' '}
          {finalAudit.summary.ambiguousPlayerCount} ambiguous player(s),{' '}
          {finalAudit.summary.noDivisionTeams} team(s) without division
        </div>
      ) : null}

      {preview?.wouldUpdate?.length ? (
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ fontSize: '0.8rem', color: '#374151' }}>
            Confirm season name to apply profile-only updates:
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={preview.season?.name || finalAudit?.season?.name || ''}
              style={{ marginLeft: 8, padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #d1d5db' }}
            />
          </label>
          <button
            type="button"
            style={{ ...btn, background: '#dc2626', color: '#fff', marginLeft: 8 }}
            disabled={loading}
            onClick={() => applyFinalSprint(false)}
          >
            Apply Final Sprint profile-only
          </button>
        </div>
      ) : null}

      {preview?.wouldUpdate?.length ? (
        <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: '0.75rem' }}>
          {preview.wouldUpdate.map((row) => (
            <div
              key={row.playerId}
              style={{ display: 'flex', gap: '0.75rem', padding: '0.4rem 0.6rem', fontSize: '0.8rem', borderBottom: '1px solid #f3f4f6' }}
            >
              <span style={{ flex: '1 1 180px', fontWeight: 600 }}>{row.playerName || row.playerId}</span>
              <span style={{ flex: '1 1 160px', color: '#4b5563' }}>{row.teamName}</span>
              <span style={{ flex: '1 1 200px', color: '#6b7280' }}>
                {row.divisionName} ({row.bandMin}–{row.bandMax})
              </span>
              <span style={{ flex: '0 0 120px', textAlign: 'right' }}>
                {row.currentProfile ?? 'none'} → <strong>{row.targetSkill}</strong>
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {preview?.ambiguousPlayers?.length ? (
        <div
          style={{
            color: '#b45309',
            fontSize: '0.85rem',
            marginBottom: '0.75rem',
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 8,
            padding: '0.6rem 0.75rem',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>
            Blocked: {preview.ambiguousPlayers.length} player(s) played in two divisions with different
            targets. Nothing can be applied until these are resolved.
          </div>
          {preview.ambiguousPlayers.map((a) => (
            <div key={a.playerId} style={{ padding: '0.2rem 0' }}>
              <strong>{a.playerName || a.playerId}</strong>
              {' — current '}
              {a.currentProfile ?? 'none'}
              {a.conflictingTargets?.length
                ? `, conflicting targets ${a.conflictingTargets.join(' vs ')}`
                : ''}
              {a.conflictingTeams?.filter(Boolean).length
                ? ` (teams: ${a.conflictingTeams.filter(Boolean).join(' / ')})`
                : ''}
              <div style={{ fontSize: '0.72rem', color: '#92400e', fontFamily: 'monospace' }}>
                {a.playerId}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default AdminSkillLevelAlignment;
